import { useMemo, useEffect } from 'react';
import { ArtistSubscriptionFragment, TrialType } from '@minecraft.graphql-operations';
import { gqlSubscription } from '@minecraft.graphql-types';
import { useModal } from '@minecraft.modal';
import { useHistory } from 'react-router';
import { PlanType } from '../usePlans';

const MODAL_OPENED_SESSION_STORAGE_KEY = 'promotionModalOpened';

export const usePromotionModal = ({
  subscription,
  previousSubscription,
  plans,
}: {
  subscription: ArtistSubscriptionFragment | gqlSubscription;
  previousSubscription?: ArtistSubscriptionFragment | gqlSubscription;
  artistId: string | number;
  plans?: PlanType[];
}) => {
  const promotionModalOpened = JSON.parse(sessionStorage.getItem(MODAL_OPENED_SESSION_STORAGE_KEY)) || false;
  const { location } = useHistory();
  const {
    isOpen: isPromotionModalOpen,
    open,
    toggleModal: togglePromotionModal,
    close: closePromotionModal,
  } = useModal();

  const discountedPlan = plans?.find((plan) => Boolean(plan?.discountPercent && plan?.discountType));
  const isOnCardlessTrialPlan = subscription?.trialType === TrialType.Cardless;
  const hasExpiredCardlessTrial =
    previousSubscription?.trialType === TrialType.Cardless && subscription?.plan.isBasePlan;

  useEffect(() => {
    if (
      (discountedPlan || isOnCardlessTrialPlan || hasExpiredCardlessTrial) &&
      !promotionModalOpened &&
      location?.pathname !== '/default-redirect'
    ) {
      sessionStorage.setItem(MODAL_OPENED_SESSION_STORAGE_KEY, 'true');
      open();
    }
  }, [promotionModalOpened, open, discountedPlan, location, isOnCardlessTrialPlan, hasExpiredCardlessTrial]);

  return useMemo(
    () => ({
      isPromotionModalOpen,
      togglePromotionModal,
      closePromotionModal,
      discountedPlan,
      isOnCardlessTrialPlan,
      hasExpiredCardlessTrial,
    }),
    [
      isPromotionModalOpen,
      togglePromotionModal,
      closePromotionModal,
      discountedPlan,
      isOnCardlessTrialPlan,
      hasExpiredCardlessTrial,
    ]
  );
};
